import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, PLAN_LABELS } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Heart, Users, Loader2, Check, Globe, Gift } from "lucide-react";
import { toast } from "sonner";

const SUGGESTED_AMOUNTS = [25, 55, 100, 150, 250];

export default function SponsorSection() {
  const [needing, setNeeding] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    sponsor_name: "",
    sponsor_email: "",
    amount: 55,
    target_registration_id: "",
    message: "",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/public/needing-sponsor?limit=24");
        setNeeding(data.registrants || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!form.sponsor_name || form.sponsor_name.length < 2) {
      toast.error("Ingresa tu nombre");
      return;
    }
    if (!form.sponsor_email || !form.sponsor_email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Monto inválido");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsorship/checkout", {
        sponsor_name: form.sponsor_name,
        sponsor_email: form.sponsor_email,
        amount,
        target_registration_id: form.target_registration_id || null,
        message: form.message || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar el patrocinio");
      setSubmitting(false);
    }
  };

  const targetReg = needing.find((r) => r.id === form.target_registration_id);

  return (
    <section className="relative bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 text-white text-xs uppercase tracking-[0.4em] font-bold mb-6">
            <Heart className="w-3 h-3 fill-white" />
            Patrocinio
          </span>
          <h2 className="font-heading uppercase text-5xl sm:text-7xl lg:text-8xl text-black leading-[0.9] tracking-tighter">
            Rescata un<br />
            <span className="text-rose-600">joven</span>, rescata una<br />
            <span className="text-gradient-gold">vida</span>
          </h2>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl mx-auto">
            Tu aporte puede ser el motivo por el que un joven viva el encuentro que cambie su vida. Patrocina a un joven en específico o suma al fondo general.
          </p>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                data-testid="sponsor-cta-button"
                className="mt-8 btn-cinematic bg-rose-600 hover:bg-rose-700 text-white font-bold text-base sm:text-lg px-10 py-7 rounded-none uppercase tracking-widest shadow-xl shadow-rose-500/30"
              >
                <Gift className="w-5 h-5 mr-2" />
                Conviértete en patrocinador
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase text-3xl text-black">Patrocinio</DialogTitle>
                <DialogDescription>Elige a quién quieres rescatar y el monto de tu aporte. Recibirás un comprobante por email.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                {/* Step 1: Choose recipient */}
                <div>
                  <Label className="text-base font-semibold">¿A quién quieres rescatar?</Label>
                  <p className="text-sm text-gray-500 mb-3">Elige una persona o aporta al fondo general (la iglesia asignará el monto a quien más lo necesite).</p>

                  <button
                    type="button"
                    onClick={() => update("target_registration_id", "")}
                    data-testid="sponsor-target-general"
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left mb-3 ${
                      !form.target_registration_id
                        ? "border-rose-500 bg-rose-50 ring-2 ring-rose-200"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">Fondo general</div>
                        <div className="text-xs text-gray-500">La iglesia decide a quién apoyar</div>
                      </div>
                      {!form.target_registration_id && <Check className="w-5 h-5 text-rose-500" />}
                    </div>
                  </button>

                  {loading ? (
                    <div className="text-center py-4 text-gray-400 text-sm">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                  ) : needing.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Aún no hay personas pendientes.</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="sponsor-targets-list">
                      {needing.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => update("target_registration_id", r.id)}
                          data-testid={`sponsor-target-${r.id}`}
                          className={`p-3 rounded-xl border-2 transition-all text-left ${
                            form.target_registration_id === r.id
                              ? "border-rose-500 bg-rose-50 ring-2 ring-rose-200"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {r.photo ? (
                              <img src={r.photo} alt={r.display_name} className="w-9 h-9 rounded-full object-cover border-2 border-rose-200" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-heading text-sm">
                                {r.display_name.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{r.display_name}</div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider truncate">
                                {r.age_category_label} · falta ${r.remaining_amount}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500" style={{ width: `${r.percentage_paid}%` }} />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 2: Amount */}
                <div>
                  <Label className="text-base font-semibold">Monto a patrocinar</Label>
                  {targetReg && (
                    <p className="text-xs text-gray-500 mb-2">
                      A {targetReg.display_name} le falta <span className="font-bold text-rose-600">${targetReg.remaining_amount}</span> para completar su rescate.
                    </p>
                  )}
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {SUGGESTED_AMOUNTS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => update("amount", a)}
                        data-testid={`sponsor-amount-${a}`}
                        className={`py-2 rounded-lg font-semibold text-sm transition-all ${
                          Number(form.amount) === a
                            ? "bg-rose-600 text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                        }`}
                      >
                        ${a}
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={form.amount}
                    onChange={(e) => update("amount", e.target.value)}
                    placeholder="O ingresa un monto personalizado"
                    className="mt-3 h-12"
                    data-testid="sponsor-amount-input"
                  />
                </div>

                {/* Sponsor info */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="sp_name">Tu nombre *</Label>
                    <Input
                      id="sp_name"
                      data-testid="sponsor-name-input"
                      value={form.sponsor_name}
                      onChange={(e) => update("sponsor_name", e.target.value)}
                      placeholder="Juan Pérez"
                      className="mt-2 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sp_email">Tu email *</Label>
                    <Input
                      id="sp_email"
                      type="email"
                      data-testid="sponsor-email-input"
                      value={form.sponsor_email}
                      onChange={(e) => update("sponsor_email", e.target.value)}
                      placeholder="tu@email.com"
                      className="mt-2 h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="sp_msg">Mensaje (opcional)</Label>
                  <Textarea
                    id="sp_msg"
                    data-testid="sponsor-message-input"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Una palabra de aliento..."
                    className="mt-2"
                    rows={3}
                  />
                </div>

                <Button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="sponsor-submit-button"
                  size="lg"
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase tracking-wider h-14 text-base"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2 fill-white" />
                      Patrocinar ${form.amount || 0}
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Preview of registrants needing help */}
        {needing.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-center gap-2 mb-6 text-sm uppercase tracking-widest text-gray-600 font-bold">
              <Users className="w-4 h-4" />
              {needing.length} jóvenes esperan tu rescate
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sponsor-preview-grid">
              {needing.slice(0, 12).map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl p-4 shadow-md hover:shadow-xl transition-shadow card-hover"
                >
                  <div className="flex justify-center mb-3">
                    {r.photo ? (
                      <img src={r.photo} alt={r.display_name} className="w-16 h-16 rounded-full object-cover border-3 border-rose-200" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-600 flex items-center justify-center text-white font-heading text-2xl">
                        {r.display_name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold truncate">{r.display_name}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{r.age_category_label}</div>
                    <div className="mt-2 text-xs text-rose-600 font-bold">Falta ${r.remaining_amount}</div>
                    <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500" style={{ width: `${r.percentage_paid}%` }} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
